<template>
  <div class="admin-page">
    <admin-page-header
      eyebrow="Kampanyalar"
      title="Link Raporları"
      description="Link üretimi, snapshot performansı ve son link akışını tek rapor yüzeyinden izleyin."
    >
      <template #actions>
        <v-btn
          outlined
          color="primary"
          small
          :loading="loading || usersLoading || userLinksLoading"
          @click="refresh"
        >
          Yenile
        </v-btn>
      </template>
    </admin-page-header>

    <div class="link-report-period mb-3">
      <v-text-field
        v-model="periodDraft.from"
        type="date"
        label="Performans başlangıcı"
        outlined
        dense
        hide-details
      ></v-text-field>
      <v-text-field
        v-model="periodDraft.to"
        type="date"
        label="Performans bitişi"
        outlined
        dense
        hide-details
      ></v-text-field>
      <v-btn
        color="primary"
        outlined
        small
        @click="applyPeriod"
      >
        Uygula
      </v-btn>
      <v-btn
        text
        small
        @click="resetPeriod"
      >
        Son 30 gün
      </v-btn>
    </div>
    <v-alert
      v-if="periodError"
      dense
      outlined
      type="error"
    >
      {{ periodError }}
    </v-alert>
    <div class="text-caption mb-4">
      Performans: {{ periodLabel }} · Link adetleri tüm geçmiş · Son linkler: en son güncellenen 50 kayıt
    </div>

    <admin-request-state
      :error="overviewError"
      :loading="loading"
      @retry="fetchOverview"
    />
    <admin-metric-strip
      v-if="overviewLoaded || loading"
      :items="summaryCards"
      :loading="loading"
    />
    <v-tabs
      v-model="activeView"
      class="admin-tabs"
    >
      <v-tab>Kullanıcılar</v-tab>
      <v-tab>Markalar</v-tab>
      <v-tab>Son linkler</v-tab>
    </v-tabs>

    <v-row>
      <v-col
        v-if="activeView === 0"
        cols="12"
      >
        <v-card
          class="admin-table-card"
          outlined
        >
          <v-toolbar
            dense
            flat
            class="admin-page-toolbar"
          >
            <v-toolbar-title>Kullanıcı performansı</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="userSearch"
              append-icon="mdi-magnify"
              label="Kullanıcı ara"
              placeholder="Kullanıcı ID, ad veya e-posta"
              single-line
              hide-details
              outlined
              dense
              clearable
              class="link-report-user-search"
              @input="onUserSearch"
            ></v-text-field>
            <v-chip
              small
              outlined
              color="primary"
              class="ml-2"
            >
              {{ usersLoading ? 'Yükleniyor' : usersError ? 'Yüklenemedi' : `${formatNumber(userTotal)} kullanıcı` }}
            </v-chip>
          </v-toolbar>
          <admin-request-state
            :error="usersError"
            :loading="usersLoading"
            @retry="fetchUsers"
          />
          <v-data-table
            v-if="!usersError"
            disable-sort
            :headers="userHeaders"
            :items="userRows"
            :loading="usersLoading"
            :server-items-length="userTotal"
            :options="userOptions"
            :footer-props="tableFooterProps"
            :item-class="userRowClass"
            class="admin-data-table elevation-0 link-report-user-table"
            @click:row="selectUser"
            @update:options="onUserOptionsUpdate"
          >
            <template v-slot:item.user="{ item }">
              <div>
                <div class="font-weight-bold">
                  {{ item.fullName || '-' }}
                </div>
                <div class="text-caption text--secondary">
                  #{{ item.userId || '-' }} · {{ item.email || '-' }}
                </div>
              </div>
            </template>
            <template v-slot:item.expectedEarnings="{ item }">
              {{ formatMoney(item.expectedEarnings) }}
            </template>
            <template v-slot:item.confirmedEarnings="{ item }">
              {{ formatMoney(item.confirmedEarnings) }}
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <v-col
        v-if="activeView === 1"
        cols="12"
      >
        <v-card
          class="admin-table-card"
          outlined
        >
          <v-toolbar
            dense
            flat
            class="admin-page-toolbar"
          >
            <v-toolbar-title>Marka performansı</v-toolbar-title>
          </v-toolbar>
          <v-data-table
            :headers="offerHeaders"
            :items="overview.topOffers || []"
            :loading="loading"
            :footer-props="tableFooterProps"
            class="admin-data-table elevation-0"
          >
            <template v-slot:item.offer="{ item }">
              <div>
                <div class="font-weight-bold">
                  {{ item.offerName || '-' }}
                </div>
                <div class="text-caption text--secondary">
                  {{ item.networkSource || '-' }}
                </div>
              </div>
            </template>
            <template v-slot:item.confirmedEarnings="{ item }">
              {{ formatMoney(item.confirmedEarnings) }}
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-card
      v-if="selectedUser && activeView === 0"
      class="admin-table-card mt-6"
      outlined
    >
      <v-toolbar
        dense
        flat
        class="admin-page-toolbar"
      >
        <v-toolbar-title>
          {{ selectedUser.fullName || '-' }} link detayları
        </v-toolbar-title>
        <v-chip
          small
          outlined
          color="primary"
          class="ml-3"
        >
          Kullanıcı #{{ selectedUser.userId }}
        </v-chip>
        <v-spacer></v-spacer>
        <v-btn
          small
          text
          :to="{ name: 'userList', query: { userId: selectedUser.userId } }"
        >
          Kullanıcı kaydı
        </v-btn>
        <v-btn
          icon
          small
          aria-label="Kullanıcı link detayını kapat"
          title="Detayı kapat"
          @click="clearSelectedUser"
        >
          <v-icon small>
            mdi-close
          </v-icon>
        </v-btn>
      </v-toolbar>
      <admin-request-state
        :error="userLinksError"
        :loading="userLinksLoading"
        @retry="fetchUserLinks"
      />
      <v-data-table
        v-if="!userLinksError"
        disable-sort
        :headers="userLinkHeaders"
        :items="userLinks"
        :loading="userLinksLoading"
        :server-items-length="userLinksTotal"
        :options="userLinkOptions"
        :footer-props="tableFooterProps"
        class="admin-data-table elevation-0"
        @update:options="onUserLinkOptionsUpdate"
      >
        <template v-slot:item.createdAt="{ item }">
          {{ formatDateTime(item.createdAt) }}
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip
            x-small
            label
            :color="statusColor(item.status)"
            :text-color="statusTextColor(item.status)"
          >
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>
        <template v-slot:item.link="{ item }">
          <div>
            <div class="font-weight-bold">
              {{ item.linkName || '-' }}
            </div>
            <div class="text-caption text--secondary">
              {{ item.uniqueId || '-' }}
            </div>
          </div>
        </template>
        <template v-slot:item.clicks="{ item }">
          {{ formatNumber(item.clicks) }}
        </template>
        <template v-slot:item.suspiciousClicks="{ item }">
          {{ formatNumber(item.suspiciousClicks) }}
        </template>
        <template v-slot:item.conversions="{ item }">
          {{ formatNumber(item.conversions) }}
        </template>
        <template v-slot:item.expectedEarnings="{ item }">
          {{ formatMoney(item.expectedEarnings) }}
        </template>
        <template v-slot:item.confirmedEarnings="{ item }">
          {{ formatMoney(item.confirmedEarnings) }}
        </template>
      </v-data-table>
    </v-card>

    <v-card
      v-if="activeView === 2"
      class="admin-table-card mt-6"
      outlined
    >
      <v-toolbar
        dense
        flat
        class="admin-page-toolbar"
      >
        <v-toolbar-title>Son linkler</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-chip
          small
          outlined
          color="primary"
        >
          {{ (overview.recentLinks || []).length }} kayıt
        </v-chip>
      </v-toolbar>
      <v-data-table
        :headers="recentHeaders"
        :items="overview.recentLinks || []"
        :loading="loading"
        :footer-props="tableFooterProps"
        class="admin-data-table elevation-0"
      >
        <template v-slot:item.user="{ item }">
          <div>
            <div class="font-weight-bold">
              {{ item.userName || '-' }}
            </div>
            <div class="text-caption text--secondary">
              {{ item.email || '-' }}
            </div>
          </div>
        </template>
        <template v-slot:item.link="{ item }">
          <div>
            <div class="font-weight-bold">
              {{ item.linkName || '-' }}
            </div>
            <div class="text-caption text--secondary">
              {{ item.uniqueId || '-' }}
            </div>
          </div>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip
            x-small
            label
            :color="statusColor(item.status)"
            :text-color="statusTextColor(item.status)"
          >
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>
        <template v-slot:item.createdAt="{ item }">
          {{ formatDateTime(item.createdAt) }}
        </template>
        <template v-slot:item.modifiedAt="{ item }">
          {{ formatDateTime(item.modifiedAt) }}
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import axios from 'axios'
import { getAccessToken } from '@/utils/panelSession'
import AdminPageHeader from '@/components/admin/AdminPageHeader.vue'
import AdminMetricStrip from '@/components/admin/AdminMetricStrip.vue'
import AdminRequestState from '@/components/admin/AdminRequestState.vue'
import { resolveApiErrorMessage } from '@/utils/apiErrors'
import { formatAdminDate } from '@/utils/adminDate'

function defaultPeriod() {
  const to = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date())
  const from = new Date(`${to}T00:00:00Z`)
  from.setUTCDate(from.getUTCDate() - 29)

  return { from: from.toISOString().slice(0, 10), to }
}

export default {
  components: {
    AdminPageHeader,
    AdminMetricStrip,
    AdminRequestState,
  },
  data: () => ({
    loading: false,
    usersLoading: false,
    userLinksLoading: false,
    activeView: 0,
    overviewError: '',
    usersError: '',
    userLinksError: '',
    overviewRequest: 0,
    usersRequest: 0,
    linksRequest: 0,
    overview: {
      summary: {},
      topUsers: [],
      topOffers: [],
      recentLinks: [],
    },
    overviewLoaded: false,
    period: defaultPeriod(),
    periodDraft: defaultPeriod(),
    periodError: '',
    userRows: [],
    userTotal: 0,
    userSearch: '',
    userSearchTimeout: null,
    userOptions: {
      page: 1,
      itemsPerPage: 20,
    },
    selectedUser: null,
    userLinks: [],
    userLinksTotal: 0,
    userLinkOptions: {
      page: 1,
      itemsPerPage: 20,
    },
    userHeaders: [
      { text: '#', value: 'rank', width: 60 },
      { text: 'Kullanıcı', value: 'user' },
      { text: 'Link', value: 'distinctLinks', width: 90 },
      { text: 'Tıklama', value: 'clicks', width: 90 },
      { text: 'Şüpheli', value: 'suspiciousClicks', width: 120 },
      { text: 'Satış', value: 'conversions', width: 90 },
      { text: 'Bekleyen', value: 'expectedEarnings', width: 130 },
      { text: 'Kesinleşen', value: 'confirmedEarnings', width: 140 },
    ],
    offerHeaders: [
      { text: '#', value: 'rank', width: 60 },
      { text: 'Marka', value: 'offer' },
      { text: 'Link', value: 'distinctLinks', width: 90 },
      { text: 'Tıklama', value: 'clicks', width: 90 },
      { text: 'Satış', value: 'conversions', width: 90 },
      { text: 'Kesinleşen', value: 'confirmedEarnings', width: 140 },
    ],
    recentHeaders: [
      { text: 'Kullanıcı', value: 'user' },
      { text: 'Link', value: 'link' },
      { text: 'Kampanya', value: 'campaignName' },
      { text: 'Marka', value: 'offerName' },
      { text: 'Durum', value: 'status', width: 120 },
      { text: 'Oluşturma', value: 'createdAt', width: 170 },
      { text: 'Güncelleme', value: 'modifiedAt', width: 170 },
    ],
    userLinkHeaders: [
      { text: 'Oluşturma T.', value: 'createdAt', width: 170 },
      { text: 'Durum', value: 'status', width: 120 },
      { text: 'Link Adı', value: 'link', width: 220 },
      { text: 'Kampanya', value: 'campaignName', width: 220 },
      { text: 'Marka', value: 'offerName', width: 220 },
      { text: 'Tıklama', value: 'clicks', width: 90 },
      { text: 'Şüpheli', value: 'suspiciousClicks', width: 120 },
      { text: 'Satış', value: 'conversions', width: 90 },
      { text: 'Bekleyen Hakediş', value: 'expectedEarnings', width: 160 },
      { text: 'Kesinleşen Hakediş', value: 'confirmedEarnings', width: 170 },
    ],
  }),
  computed: {
    periodLabel() {
      return `${this.period.from.split('-').reverse().join('.')} – ${this.period.to.split('-').reverse().join('.')}`
    },
    requestOptions() {
      return {
        headers: { Authorization: `Bearer ${getAccessToken()}` },
      }
    },
    tableFooterProps() {
      return {
        'items-per-page-options': [10, 20, 50, 100],
      }
    },
    summaryCards() {
      const summary = this.overview.summary || {}

      return [
        {
          label: 'Toplam Link',
          value: this.formatNumber(summary.totalLinks),
          helper: `${this.formatNumber(summary.activeLinks)} aktif / ${this.formatNumber(summary.passiveLinks)} pasif`,
        },
        {
          label: 'Dönem tıklaması',
          value: this.formatNumber(summary.clicksLast30Days),
          helper: `${this.formatNumber(summary.suspiciousClicksLast30Days)} şüpheli`,
        },
        {
          label: 'Dönem satışı',
          value: this.formatNumber(summary.conversionsLast30Days),
          helper: this.periodLabel,
        },
        {
          label: 'Beklenen Kazanç',
          value: this.formatMoney(summary.expectedEarningsLast30Days),
          helper: this.periodLabel,
        },
        {
          label: 'Kesinleşen kazanç',
          value: this.formatMoney(summary.confirmedEarningsLast30Days),
          helper: this.periodLabel,
        },
        {
          label: 'Son veri güncellemesi',
          value: this.formatDateTime(summary.lastSnapshotSyncAt),
          helper: `${this.formatNumber(summary.completedLinks)} tamamlanmış link`,
        },
      ]
    },
  },
  mounted() {
    this.refresh()
  },
  beforeDestroy() {
    clearTimeout(this.userSearchTimeout)
    this.overviewRequest += 1
    this.usersRequest += 1
    this.linksRequest += 1
  },
  methods: {
    applyPeriod() {
      const { from, to } = this.periodDraft
      const valid = value => /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`)) && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value
      if (!valid(from) || !valid(to) || from > to) {
        this.periodError = 'Geçerli bir başlangıç ve bitiş tarihi seçin.'

        return undefined
      }
      this.periodError = ''
      this.period = { from, to }
      this.userOptions = { ...this.userOptions, page: 1 }
      this.userLinkOptions = { ...this.userLinkOptions, page: 1 }
      this.overviewLoaded = false
      this.overview = {
        summary: {}, topUsers: [], topOffers: [], recentLinks: [],
      }

      return this.refresh()
    },
    resetPeriod() {
      this.periodDraft = defaultPeriod()

      return this.applyPeriod()
    },
    refresh() {
      return Promise.all([this.fetchOverview(), this.fetchUsers(), this.fetchUserLinks()])
    },
    async fetchOverview() {
      this.overviewRequest += 1
      const request = this.overviewRequest
      this.loading = true
      this.overviewError = ''
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}admin/link-reports/overview`,
          { ...this.requestOptions, params: { includeTopUsers: false, ...this.period } },
        )
        if (request !== this.overviewRequest) return
        const payload = response?.data?.data
        if (!payload || !payload.summary) throw new Error('Invalid report response')
        this.overview = payload
        this.overviewLoaded = true
      } catch (error) {
        if (request !== this.overviewRequest) return
        this.overviewError = resolveApiErrorMessage(error, 'Link özeti yüklenemedi.')
      } finally {
        if (request === this.overviewRequest) this.loading = false
      }
    },
    async fetchUsers() {
      this.usersRequest += 1
      const request = this.usersRequest
      this.usersLoading = true
      this.usersError = ''
      this.userRows = []

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}admin/link-reports/users`,
          {
            ...this.requestOptions,
            params: {
              ...this.period,
              search: (this.userSearch || '').trim(),
              page: this.resolveServerPage(this.userOptions),
              size: this.resolvePageSize(this.userOptions),
            },
          },
        )
        if (request !== this.usersRequest) return
        const payload = response?.data?.data || {}
        this.userRows = payload.content || []
        this.userTotal = Number(payload.totalElements || 0)
      } catch (error) {
        if (request !== this.usersRequest) return
        this.usersError = resolveApiErrorMessage(error, 'Kullanıcı link raporu yüklenemedi.')
      } finally {
        if (request === this.usersRequest) this.usersLoading = false
      }
    },
    async fetchUserLinks() {
      if (!this.selectedUser || !this.selectedUser.userId) {
        return
      }

      this.userLinksLoading = true
      this.linksRequest += 1
      const request = this.linksRequest
      this.userLinksError = ''
      this.userLinks = []

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}admin/link-reports/users/${this.selectedUser.userId}/links`,
          {
            ...this.requestOptions,
            params: {
              ...this.period,
              page: this.resolveServerPage(this.userLinkOptions),
              size: this.resolvePageSize(this.userLinkOptions),
            },
          },
        )
        if (request !== this.linksRequest) return
        const payload = response?.data?.data || {}
        this.userLinks = payload.content || []
        this.userLinksTotal = Number(payload.totalElements || 0)
      } catch (error) {
        if (request !== this.linksRequest) return
        this.userLinksError = resolveApiErrorMessage(error, 'Kullanıcı link detayları yüklenemedi.')
      } finally {
        if (request === this.linksRequest) this.userLinksLoading = false
      }
    },
    onUserOptionsUpdate(options) {
      if (!options || !options.page) return
      if (options.page === this.userOptions.page && options.itemsPerPage === this.userOptions.itemsPerPage) return
      this.userOptions = {
        ...this.userOptions,
        ...options,
      }
      this.fetchUsers()
    },
    onUserLinkOptionsUpdate(options) {
      if (!options || !options.page || !this.selectedUser) return
      if (options.page === this.userLinkOptions.page && options.itemsPerPage === this.userLinkOptions.itemsPerPage) return
      this.userLinkOptions = {
        ...this.userLinkOptions,
        ...options,
      }
      this.fetchUserLinks()
    },
    onUserSearch() {
      clearTimeout(this.userSearchTimeout)
      this.userSearchTimeout = setTimeout(() => {
        this.userOptions = {
          ...this.userOptions,
          page: 1,
        }
        this.fetchUsers()
      }, 250)
    },
    selectUser(item) {
      this.selectedUser = item
      this.userLinkOptions = {
        ...this.userLinkOptions,
        page: 1,
      }
      this.fetchUserLinks()
    },
    clearSelectedUser() {
      this.linksRequest += 1
      this.userLinksLoading = false
      this.userLinksError = ''
      this.selectedUser = null
      this.userLinks = []
      this.userLinksTotal = 0
    },
    userRowClass(item) {
      if (!this.selectedUser || item.userId !== this.selectedUser.userId) {
        return 'link-report-user-row'
      }

      return 'link-report-user-row link-report-user-row--selected'
    },
    resolveServerPage(options) {
      return Math.max(Number(options?.page || 1) - 1, 0)
    },
    resolvePageSize(options) {
      const size = Number(options?.itemsPerPage || 20)

      return size > 0 ? size : 20
    },
    formatMoney(value) {
      const numeric = Number(value || 0)

      return `₺${new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numeric)}`
    },
    formatNumber(value) {
      return new Intl.NumberFormat('tr-TR').format(Number(value || 0))
    },
    formatDateTime(value) {
      return formatAdminDate(value)
    },
    statusColor(status) {
      if (status === 'ACTIVE') return 'success'
      if (status === 'PASSIVE') return 'warning'
      if (status === 'COMPLETED') return 'secondary'

      return 'grey'
    },
    statusTextColor(status) {
      return status === 'PASSIVE' ? 'black' : 'white'
    },
    statusLabel(status) {
      if (status === 'ACTIVE') return 'Aktif'
      if (status === 'PASSIVE') return 'Pasif'
      if (status === 'COMPLETED') return 'Tamamlandı'

      return status || '-'
    },
  },
}
</script>

<style scoped>
.link-report-period {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}
.link-report-period .v-input {
  flex: 1 1 180px;
  max-width: 250px;
}
@media (max-width: 600px) {
  .link-report-period .v-input { max-width: none; }
}
.link-report-user-search {
  max-width: 360px;
}

.link-report-user-table ::v-deep tbody tr {
  cursor: pointer;
}

.link-report-user-table ::v-deep tbody tr.link-report-user-row--selected {
  background: #fff5f5;
}
</style>
